import type { OutreachParsedBundle } from '@discovr/types';
import { fmtDeliverableType } from '@discovr/types';
import { fmtRate } from './QuoteBlock';

type Bundle = Pick<OutreachParsedBundle, 'amount' | 'currency' | 'perUnit' | 'items' | 'freeIds'> & { deliverableIds?: string[] };

const isFree = (bundle: Bundle, id: string, amount?: number): boolean => amount === 0 || (bundle.freeIds ?? []).includes(id);

/** What the creator quoted for a package, split into the priced pieces and the free ones. */
export function bundleParts(bundle: Bundle) {
  const items = bundle.items ?? [];
  const paid = items.filter((i) => !isFree(bundle, i.deliverableId, i.amount));
  const free = items.filter((i) => isFree(bundle, i.deliverableId, i.amount));
  const freeOnlyIds = (bundle.freeIds ?? []).filter((id) => !items.some((i) => i.deliverableId === id));
  const freeLabels = [...free.map((i) => fmtDeliverableType(i.deliverableId)), ...freeOnlyIds.map(fmtDeliverableType)];
  const pieces = paid.reduce((n, i) => n + i.count, 0);
  return { items, paid, free, freeLabels, pieces };
}

/** One line for the Quote card: a per-piece price shows its multiplier and total, a package shows its counts, free items say free. */
export function bundleLine(bundle: Bundle): string {
  const money = fmtRate(bundle.amount, bundle.currency);
  const { items, paid, free, freeLabels, pieces } = bundleParts(bundle);
  const freeText = freeLabels.length ? `; ${freeLabels.join(' + ')} free` : '';
  if (bundle.perUnit && pieces > 0) {
    const what = paid.map((i) => `${i.count} ${fmtDeliverableType(i.deliverableId)}`).join(' + ');
    return `${money} each × ${what} (${fmtRate(bundle.amount * pieces, bundle.currency)})${freeText}`;
  }
  if (items.length > 0) {
    const parts = [...paid.map((i) => `${i.count} × ${fmtDeliverableType(i.deliverableId)}`), ...free.map((i) => `${i.count} × ${fmtDeliverableType(i.deliverableId)} (free)`)];
    return `${parts.join(' + ')} for ${money}`;
  }
  return `${money} for ${(bundle.deliverableIds ?? []).map(fmtDeliverableType).join(' + ')}`;
}

/** The Agree dialog's statement of what was quoted; null when the quote is a plain package total. */
export function perUnitQuoteText(bundle: Bundle): string | null {
  if (!bundle.perUnit) return null;
  const { paid, freeLabels, pieces } = bundleParts(bundle);
  if (pieces === 0) return null;
  const what = paid.map((i) => fmtDeliverableType(i.deliverableId)).join(' + ');
  return `${fmtRate(bundle.amount, bundle.currency)} per ${what}, ${pieces} ${pieces === 1 ? 'piece' : 'pieces'}${freeLabels.length ? `; ${freeLabels.join(' + ')} free` : ''}`;
}
